/**
 * Оформление подписки: тариф, период, способ оплаты, оферта, согласия.
 *
 * **Способов оплаты два, и второй — не украшение.** На российском рынке
 * половина клиентов платит по счёту, а закупка юрлица без счёта и закрывающих
 * не проходит вовсе. Роут `POST /billing/documents` был в контракте с самого
 * начала, экран тарифа обещал «Оплата по счёту — обычный путь для юрлица», а
 * вызвать его было неоткуда: кнопки не существовало ни на одном экране.
 * Приёмка нашла обещание, не подкреплённое ничем.
 *
 * Оба пути ведут к одной подписке и различаются только тем, чем платят:
 * картой на стороне ЮKassa или переводом по выставленному счёту. Счёт
 * открывает подписку сразу, но включается она по факту поступления денег — об
 * этом экран говорит до нажатия, а не после.
 *
 * Что здесь обязано быть именно так.
 *
 * **Оферта принимается действием, а не мелким шрифтом.** Согласие — активный
 * чекбокс, который гейтит отправку (спека §14.2). Пассивная строка «нажимая
 * кнопку, вы соглашаетесь» — не согласие; на российском рынке это ещё и
 * проверяемое требование, а не вкус дизайнера.
 *
 * **Автосписание — отдельное согласие.** 376-ФЗ требует и согласия на
 * автоматические списания, и уведомления за 24 часа до каждого. Поэтому галочка
 * «запомнить карту» несёт свой текст и свою версию редакции, а не прячется в
 * общем согласии с офертой.
 *
 * **Ключ идемпотентности создаётся один раз на попытку.** Оплата требует
 * подтверждения на действии; после подтверждения запрос повторяется — и должен
 * повториться с тем же ключом, иначе клиент получит два платежа.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Field, Kv, KvRow, Panel } from '../ui/form';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import { LEGAL, LEGAL_LINK_PROPS } from '../legal';
import {
  createCheckout,
  createInvoice,
  downloadDocument,
  getBillingPortal,
  getPlanCatalog,
  idempotencyKey,
  rememberPayment,
  returnUrl,
  type BillingDocument,
  type BillingPeriodUnit,
  type PlanCatalog,
} from '../api/billing';
import { countOf, dateProse, moneyKopecks } from '@shared/src/format';
import { PAYER_SNAPSHOT } from '@shared/src/product';
import {
  INVOICE_HELD_REASON,
  checkoutGate,
  cardAllowed,
  cardHeldReason,
  effectiveMethod,
  type Method,
} from './checkout-gate';

/** Порядок тарифов в сетке. В контракте поле необязательное. */
const order = (plan: { sort_order?: number }) => plan.sort_order ?? 0;

/**
 * Цена или «по договору» — **одним** выражением на весь экран.
 *
 * По контракту `yearly_price_kopecks` — `[integer, null]`, и `?? 0` в плитке
 * превращал «цены нет» в «0 ₽/год»: клиент, переключивший период на годовой,
 * видел у договорного тарифа ноль в плитке и «по договору» в панели оплаты
 * внизу той же страницы — два разных ответа про одну цену на одном экране. В
 * РФ страница оформления работает вместе с офертой, и показанный ноль там не
 * косметика, а цена, которую нам предъявят.
 *
 * Плитки договорных тарифов при этом больше не отфильтровываются из списка:
 * фильтр прятал их целиком, из-за чего ветка «по договору» в панели была
 * недостижима — то есть код, написанный ради этого случая, никогда не
 * исполнялся.
 */
function priceProse(kopecks: number | null | undefined): string {
  return kopecks === null || kopecks === undefined
    ? 'по договору'
    : moneyKopecks(kopecks);
}

/** Редакция текста согласия на автосписание. Уезжает в запрос и хранится с ним. */
const AUTOPAY_TEXT_VERSION = '2026-06-01';

/**
 * Редакция публичной оферты, которую принимает клиент.
 *
 * Уезжает в запрос и ложится в `billing.offer_acceptances` вместе с моментом,
 * IP и User-Agent. Менять значение можно только вместе с текстом документа на
 * лендинге: по нему потом доказывают, ЧТО именно человек принял.
 */
const OFFER_TEXT_VERSION = '2026-06-01';

type Load =
  | { kind: 'loading' }
  // `invoice` — можно ли предлагать оплату по счёту. При брошенной оплате
  // (`pending`) форма открыта ради карты, а счёт сервер откажет: см.
  // `checkout-gate.ts`.
  | { kind: 'ready'; catalog: PlanCatalog; invoice: boolean }
  // Подписка уже есть. Отдельное состояние, а не отказ на кнопке: см. загрузку.
  | { kind: 'occupied'; planTitle: string | null }
  | { kind: 'failed'; error: unknown };

/**
 * Реквизиты плательщика.
 *
 * Уезжают в `payer_details` и **копируются в документ**: организация переедет,
 * а подписанный счёт обязан остаться таким, каким его подписали. Поэтому здесь
 * не ссылка на организацию, а слепок полей.
 *
 * Обязательны два: название юрлица и ИНН. Без них документ бухгалтерию не
 * пройдёт, и выставлять его — значит делать вид, что продажа состоялась.
 */
interface Payer {
  legal_name: string;
  inn: string;
  kpp: string;
  legal_address: string;
}

const EMPTY_PAYER: Payer = { legal_name: '', inn: '', kpp: '', legal_address: '' };

/**
 * Проверка реквизитов до отправки.
 *
 * ИНН — 10 знаков у юрлица и 12 у ИП; контрольную сумму здесь не считаем, но
 * длину и состав проверяем: опечатка в ИНН обнаруживается бухгалтерией клиента
 * через неделю, а исправить выписанный документ уже нельзя.
 */
function payerProblems(payer: Payer): Partial<Record<keyof Payer, string>> {
  const problems: Partial<Record<keyof Payer, string>> = {};
  if (!payer.legal_name.trim())
    problems.legal_name = 'Без названия юрлица счёт выставить не на кого';
  const inn = payer.inn.trim();
  if (!inn) problems.inn = 'ИНН обязателен: по нему платёж находят в банке';
  else if (!/^\d{10}$|^\d{12}$/.test(inn))
    problems.inn = 'ИНН — 10 цифр у организации или 12 у ИП';
  const kpp = payer.kpp.trim();
  if (kpp && !/^\d{9}$/.test(kpp)) problems.kpp = 'КПП — девять цифр';
  return problems;
}

/**
 * Переключатель способа оплаты вместе с подписью под ним.
 *
 * Вынесен отдельным экспортом ровно затем, чтобы приёмка
 * (`tools/check-invoice-gate.mjs`) могла его ОТРИСОВАТЬ, а не пересказать:
 * проверять таблицу правил мало — дефект был не в правиле, а в том, что экран
 * предлагал нажать то, что сервер откажет. Данные `Checkout` берёт в
 * `useEffect`, а эффекты на сервере не выполняются, поэтому проверяемая часть
 * живёт здесь и рисуется на готовых значениях.
 */
export function PaymentMethods({
  method,
  invoiceOffered,
  cardOffered = true,
  cardReason,
  busy,
  onChoose,
}: {
  method: Method;
  /** Можно ли предлагать счёт: при занятой подписке сервер откажет всегда. */
  invoiceOffered: boolean;
  /** Можно ли предлагать карту: выше потолка ЮKassa платёж умрёт на её странице. */
  cardOffered?: boolean;
  /** Почему карты нет — текст обязан прийти вместе с запретом, а не отдельно. */
  cardReason?: string;
  busy: boolean;
  onChoose: (method: Method) => void;
}) {
  return (
    <>
      <fieldset className="seg-radio wide" aria-describedby="pay-method-hint">
        <legend className="visually-hidden">Способ оплаты</legend>
        {(['card', 'invoice'] as const).map((unit) => (
          <label key={unit}>
            <input
              type="radio"
              name="method"
              value={unit}
              checked={method === unit}
              // Отключённый переключатель, а не спрятанный: путь по счёту
              // существует и остаётся обычным, недоступен он ровно сейчас и по
              // названной причине. Спрятать значило бы сделать вид, что оплаты
              // по счёту у нас нет вовсе.
              disabled={
                busy ||
                (unit === 'invoice' && !invoiceOffered) ||
                (unit === 'card' && !cardOffered)
              }
              onChange={() => onChoose(unit)}
            />
            <span>{unit === 'card' ? 'Картой через ЮKassa' : 'По счёту на юрлицо'}</span>
          </label>
        ))}
      </fieldset>

      <p className="hint" id="pay-method-hint">
        {!cardOffered
          ? cardReason
          : !invoiceOffered
            ? INVOICE_HELD_REASON
            : method === 'card'
              ? 'Подписка включается сразу после подтверждения банком, обычно за несколько секунд'
              : 'Счёт выставляется сразу, подписка включается по факту поступления денег'}
      </p>
    </>
  );
}

export function Checkout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [planCode, setPlanCode] = useState(params.get('plan') ?? 'growth');
  const [period, setPeriod] = useState<BillingPeriodUnit>(
    params.get('period') === 'year' ? 'year' : 'month',
  );
  // Способ приходит из адреса: экран тарифа ведёт сюда обеими кнопками, и
  // человек, нажавший «Выставить счёт», обязан попасть на нужную форму сразу.
  //
  // Это ВЫБОР человека, а не действующий способ: при незакрытой оплате счёта
  // нет вовсе, и тогда выбор ничего не значит — см. `method` ниже.
  const [methodChoice, setMethodChoice] = useState<Method>(
    params.get('method') === 'invoice' ? 'invoice' : 'card',
  );
  const [payer, setPayer] = useState<Payer>(EMPTY_PAYER);
  const [payerErrors, setPayerErrors] = useState<Partial<Record<keyof Payer, string>>>(
    {},
  );
  const [invoice, setInvoice] = useState<BillingDocument | null>(null);
  // Скачивание счёта — отдельная пара состояний: оно происходит уже ПОСЛЕ
  // успешной выписки, и его отказ не должен стирать сам выставленный счёт.
  const [downloading, setDownloading] = useState(false);
  const [downloadFailure, setDownloadFailure] = useState<unknown>(null);
  const [offer, setOffer] = useState(false);
  // Предотмеченная галочка под РЕГУЛЯРНЫЕ списания — сомнительная практика:
  // согласие на повторяющийся платёж человек даёт сам, а не находит уже данным.
  // Оферта рядом при этом снята — две отметки одного экрана вели себя по-разному.
  const [autopay, setAutopay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [offerError, setOfferError] = useState(false);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Отказ обязан попасть в поле зрения и в фокус: иначе он существует только в
  // разметке. Скролл — плавный и один раз на появление ошибки.
  useEffect(() => {
    if (!error) return;
    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    errorRef.current?.focus({ preventScroll: true });
  }, [error]);

  // Состояние подписки спрашивается ВМЕСТЕ с каталогом, до первого поля.
  //
  // Раньше экран знал только каталог: человек с действующей подпиской выбирал
  // тариф, вбивал название юрлица, ИНН и КПП, ставил обе галочки — и получал
  // «У организации уже есть подписка» на кнопке. Форма, которую нельзя было
  // отправить с самого начала, спрашивала реквизиты десять полей подряд.
  // Проверка стоит здесь ровно потому, что ответ известен до формы.
  useEffect(() => {
    const abort = new AbortController();
    Promise.all([getPlanCatalog(abort.signal), getBillingPortal(abort.signal)])
      .then(([catalog, portal]) => {
        // Правило берётся у сервера, а не придумывается здесь, и живёт одной
        // функцией на оба вопроса — пускать ли к форме и можно ли платить по
        // счёту: `checkout-gate.ts`.
        const held = portal.subscription;
        const gate = checkoutGate(held?.status);
        setLoad(
          gate.occupied
            ? { kind: 'occupied', planTitle: held?.plan_title ?? null }
            : { kind: 'ready', catalog, invoice: gate.invoice },
        );
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoad({ kind: 'failed', error: caught });
      });
    return () => abort.abort();
  }, []);

  const head = (
    <div className="screen-head">
      <h1>Оформление подписки</h1>
      <Link className="btn btn-ghost btn-sm" to="/plan">
        Вернуться к тарифу
      </Link>
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }
  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice error={load.error} />
      </>
    );
  }
  if (load.kind === 'occupied') {
    return (
      <>
        {head}
        <Empty
          eyebrow="Подписка уже оформлена"
          title={
            load.planTitle
              ? `Сейчас действует «${load.planTitle}»`
              : 'У организации уже есть действующий тариф'
          }
          action={
            <Link className="btn btn-primary" to="/plan">
              Открыть тариф
            </Link>
          }
        >
          Вторую подписку на одну организацию мы не заводим. Сменить тариф, докупить
          вопросы или отменить продление можно на экране «Тариф» — там же лежат счета и
          история платежей.
        </Empty>
      </>
    );
  }

  const invoiceOffered = load.invoice;

  const plans = load.catalog.plans.slice().sort((a, b) => order(a) - order(b));
  const plan = plans.find((p) => p.code === planCode) ?? plans[0];

  // Справочник пуст только если сервис отдал пустой каталог: платить не за что,
  // и притворяться, что есть, нельзя.
  if (!plan) {
    return (
      <>
        {head}
        <ErrorNotice error={null} />
      </>
    );
  }

  // Отдельная переменная, а не сужение по месту: обработчик отправки — функция,
  // и внутри неё сужение по `if (!plan)` уже не действует.
  const chosen = plan;
  const amount =
    period === 'year' ? chosen.yearly_price_kopecks : chosen.monthly_price_kopecks;

  // Способ оплаты считается ПОСЛЕ суммы, потому что от суммы и зависит: выше
  // потолка ЮKassa карты нет вовсе. Выбор человека здесь не просто игнорируется
  // на кнопке, а не действует вовсе — и форма реквизитов, и текст полосы
  // оплаты, и сама отправка идут по `method`. Прийти сюда можно с
  // `?method=invoice` — ссылкой из письма или закладкой.
  const cardLimit = load.catalog.card_payment_max_kopecks;
  const cardOffered = cardAllowed(amount, cardLimit);
  const method = effectiveMethod(
    methodChoice,
    { occupied: false, invoice: invoiceOffered },
    cardOffered,
  );

  /** Реквизиты для запроса: пустые поля не отправляются вовсе. */
  function payerDetails(): Record<string, string> {
    return Object.fromEntries(
      Object.entries(payer)
        .map(([field, value]) => [field, value.trim()])
        .filter(([, value]) => value !== ''),
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!offer) {
      setOfferError(true);
      return;
    }
    if (method === 'invoice') {
      const problems = payerProblems(payer);
      setPayerErrors(problems);
      if (Object.keys(problems).length > 0) return;
    }
    setOfferError(false);
    setBusy(true);
    setError(null);

    // Один ключ на одну попытку оплаты: повтор после подтверждения обязан быть
    // тем же запросом, иначе у клиента появится второй платёж.
    const key = idempotencyKey();

    try {
      if (method === 'invoice') {
        // Счёт не требует подтверждения на действии: денег он не списывает,
        // способ оплаты не сохраняет и карту не трогает. Требовать пароль там,
        // где ничего не происходит, — это приучать подтверждать не глядя.
        setInvoice(
          await createInvoice(
            {
              plan_code: chosen.code,
              period,
              payer_details: payerDetails(),
              // Акцепт оферты обязателен и здесь: договор заключается отметкой,
              // а не способом оплаты. Сервер пишет версию текста, момент, IP и
              // User-Agent — так же, как при оплате картой.
              offer_consent: { text_version: OFFER_TEXT_VERSION, accepted: true },
            },
            key,
          ),
        );
        return;
      }

      // Подтверждения на действии здесь больше нет: оно стояло ровно там, где
      // клиент расстаётся с деньгами, и замыкало путь учётной записи без пароля
      // (вход через Яндекс ID) в тупик «Подтверждение сейчас недоступно».
      // Платёж защищает ЮKassa.
      const checkout = await createCheckout(
        {
          plan_code: chosen.code,
          period,
          return_url: returnUrl(),
          save_payment_method: autopay,
          // Акцепт оферты записывается сервером — версия текста, момент, IP и
          // User-Agent. До этого согласие оставалось только в браузере, то
          // есть договор был недоказуем.
          offer_consent: { text_version: OFFER_TEXT_VERSION, accepted: true },
          autopay_consent: autopay
            ? { text_version: AUTOPAY_TEXT_VERSION, accepted: true }
            : null,
          ...(payer.legal_name.trim() ? { payer_details: payerDetails() } : {}),
        },
        key,
      );

      // Идентификатор платежа запоминаем **до** ухода на платёжку — запасным
      // путём к экрану возврата. Основной теперь надёжнее: `payment_id`
      // дописывает сервер в адрес, который уезжает провайдеру.
      rememberPayment(checkout.payment_id);

      window.location.assign(checkout.confirmation_url);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  // Счёт выставлен: форма закончилась, дальше человек идёт в свою бухгалтерию.
  // Экран говорит номер, сумму и что будет дальше — и не обещает подписку,
  // которой ещё нет.
  if (invoice) {
    return (
      <>
        {head}
        <Panel
          title={`Счёт № ${invoice.number} выставлен`}
          lead="Подписка включится по факту поступления денег. Обычно это следующий рабочий день после платежа, но зависит от вашего банка."
        >
          <Kv>
            <KvRow label="Сумма">{moneyKopecks(invoice.amount_kopecks)}</KvRow>
            <KvRow label="Тариф">
              {chosen.title} · {period === 'year' ? 'год' : 'месяц'}
            </KvRow>
            <KvRow label="Плательщик" note={`ИНН ${payer.inn.trim()}`}>
              {payer.legal_name.trim()}
            </KvRow>
            {invoice.issued_on ? (
              <KvRow label="Выставлен">{dateProse(invoice.issued_on)}</KvRow>
            ) : null}
          </Kv>

          <p className="prose-note">
            {PAYER_SNAPSHOT} Ошиблись — выставьте счёт заново: прежний станет
            аннулированным, и оплачивать нужно будет только новый.
          </p>

          <div className="row-actions">
            <button
              className="btn btn-primary"
              type="button"
              disabled={downloading}
              onClick={() => {
                setDownloading(true);
                setDownloadFailure(null);
                downloadDocument(invoice.id)
                  .catch(setDownloadFailure)
                  .finally(() => setDownloading(false));
              }}
            >
              {downloading ? 'Готовим счёт…' : 'Скачать счёт'}
            </button>
            {/* Перевыставление — обычное действие, а не обращение в поддержку:
                опечатка в ИНН попадается на первом же счёте. Панель просто
                убирается, и человек оказывается на той же форме с теми же
                реквизитами. */}
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => setInvoice(null)}
            >
              Выставить заново
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => navigate('/plan')}
            >
              К тарифу
            </button>
          </div>

          {downloadFailure ? <ErrorNotice error={downloadFailure} /> : null}

          <p className="stamp">
            Счёт и закрывающие всегда доступны на экране тарифа, в разделе «Счета и
            закрывающие». Если файл ещё не сформирован, скачивание об этом скажет — номер
            и сумма уже действуют, документ подтянется.
          </p>
        </Panel>
      </>
    );
  }

  return (
    <>
      {head}

      {/* Копия отказа наверху остаётся: с неё начинается страница, и человек,
          вернувшийся к форме, видит причину сразу. Но решение принимается внизу,
          у кнопки, — там же стоит вторая копия (см. `pay-bar`). */}
      {error ? <ErrorNotice error={error} /> : null}

      {/* Пока запрос в пути, заказ не меняется.
          Отправка замыкает тариф, период и способ на значениях момента нажатия,
          а экран продолжал их слушать: переключив тариф до ответа, человек
          получал панель «Счёт выставлен» с НОВЫМ названием тарифа поверх счёта,
          выписанного на старый, — то есть экран называл не то, что в документе.
          Со способом оплаты хуже: выбрав карту, можно было уехать на ЮKassa
          из-за нажатия, сделанного по счёту. */}
      <form onSubmit={submit} noValidate>
        <Panel title="Что оформляем">
          <div className="choices">
            {plans.map((option) => (
              <label
                key={option.code}
                className={`choice${option.code === chosen.code ? ' on' : ''}`}
              >
                <input
                  type="radio"
                  name="plan"
                  value={option.code}
                  checked={option.code === chosen.code}
                  disabled={busy}
                  onChange={() => setPlanCode(option.code)}
                />
                <span className="choice-body">
                  <b>{option.title}</b>
                  {/* Ежедневный пульс отсюда убран, и это самое важное место
                      из всех, где он стоял: строка описывает тариф ровно в тот
                      момент, когда человек ставит галочку «принимаю оферту», а
                      оферта обещает еженедельные замеры. Купленной квотой
                      пульса покупатель считал бы её совершенно разумно, и
                      толковалось бы это против составителя договора. */}
                  <span className="stamp">
                    {countOf(
                      option.tracked_questions,
                      'отслеживаемый вопрос',
                      'отслеживаемых вопроса',
                      'отслеживаемых вопросов',
                    )}{' '}
                    · {countOf(option.history_months, 'месяц', 'месяца', 'месяцев')}{' '}
                    истории
                  </span>
                </span>
                {(() => {
                  const kopecks =
                    period === 'year'
                      ? option.yearly_price_kopecks
                      : option.monthly_price_kopecks;
                  const contractual = kopecks === null || kopecks === undefined;
                  return (
                    <span className="choice-price tnum">
                      {priceProse(kopecks)}
                      {/* «/год» ставится к сумме, а не к словам: «по
                            договору/год» — это не период у цены, а мусор
                            рядом с фразой, которая период и не называет. */}
                      {contractual ? null : (
                        <small>{period === 'year' ? '/год' : '/мес'}</small>
                      )}
                    </span>
                  );
                })()}
              </label>
            ))}
          </div>

          <fieldset className="seg-radio wide">
            <legend className="visually-hidden">Период оплаты</legend>
            {(['month', 'year'] as const).map((unit) => (
              <label key={unit}>
                <input
                  type="radio"
                  name="period"
                  value={unit}
                  checked={period === unit}
                  disabled={busy}
                  onChange={() => setPeriod(unit)}
                />
                <span>
                  {unit === 'month' ? 'Помесячно' : 'На год, два месяца в подарок'}
                </span>
              </label>
            ))}
          </fieldset>
        </Panel>

        <Panel
          title="Чем платите"
          lead="Счёт и закрывающие документы — обычный путь для юрлица. Половина клиентов на российском рынке платит именно так."
        >
          <PaymentMethods
            method={method}
            invoiceOffered={invoiceOffered}
            cardOffered={cardOffered}
            cardReason={cardHeldReason(cardLimit)}
            busy={busy}
            onChoose={(unit) => {
              setMethodChoice(unit);
              setPayerErrors({});
              setError(null);
            }}
          />

          {method === 'invoice' ? (
            <>
              <Field
                id="payer-name"
                label="Название юрлица"
                hint="Как в уставе: «ООО „Ромашка“», а не «Ромашка»"
                error={payerErrors.legal_name}
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    autoComplete="organization"
                    value={payer.legal_name}
                    onChange={(event) =>
                      setPayer({ ...payer, legal_name: event.target.value })
                    }
                  />
                )}
              </Field>

              <Field
                id="payer-inn"
                label="ИНН"
                hint="10 цифр у организации, 12 у индивидуального предпринимателя"
                error={payerErrors.inn}
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    inputMode="numeric"
                    value={payer.inn}
                    onChange={(event) =>
                      setPayer({ ...payer, inn: event.target.value.replace(/\D/g, '') })
                    }
                  />
                )}
              </Field>

              <Field
                id="payer-kpp"
                label="КПП"
                optional
                hint="Девять цифр. У индивидуального предпринимателя КПП нет"
                error={payerErrors.kpp}
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    inputMode="numeric"
                    value={payer.kpp}
                    onChange={(event) =>
                      setPayer({ ...payer, kpp: event.target.value.replace(/\D/g, '') })
                    }
                  />
                )}
              </Field>

              <Field
                id="payer-address"
                label="Юридический адрес"
                optional
                hint="Попадёт в счёт и закрывающие"
                error={payerErrors.legal_address}
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    autoComplete="street-address"
                    value={payer.legal_address}
                    onChange={(event) =>
                      setPayer({ ...payer, legal_address: event.target.value })
                    }
                  />
                )}
              </Field>

              <p className="prose-note">
                {PAYER_SNAPSHOT} Проверьте их до выставления — исправить выписанный
                документ нельзя, можно только выставить новый.
              </p>
            </>
          ) : (
            <Field
              id="payer-card"
              label="Название юрлица"
              optional
              hint="Оставьте пустым, если платите картой как физлицо"
            >
              {(props) => (
                <input
                  {...props}
                  type="text"
                  autoComplete="organization"
                  value={payer.legal_name}
                  onChange={(event) =>
                    setPayer({ ...payer, legal_name: event.target.value })
                  }
                />
              )}
            </Field>
          )}
        </Panel>

        <Panel title="Согласия">
          <label className={`consent${offerError ? ' invalid' : ''}`}>
            <input
              type="checkbox"
              name="offer"
              checked={offer}
              aria-invalid={offerError ? true : undefined}
              aria-describedby="offer-error"
              onChange={(event) => {
                setOffer(event.target.checked);
                if (event.target.checked) setOfferError(false);
              }}
            />
            <span>
              Принимаю условия{' '}
              <a href={LEGAL.offer} {...LEGAL_LINK_PROPS}>
                публичной оферты
              </a>{' '}
              и{' '}
              <a href={LEGAL.terms} {...LEGAL_LINK_PROPS}>
                пользовательского соглашения
              </a>
              , согласен на{' '}
              {/* Отметка даёт согласие — значит и ведёт она в текст согласия
                  (`/legal/`), а не в политику конфиденциальности. До этого обе
                  ссылки на сайте вели не туда, куда обещали подписи. */}
              <a href={LEGAL.consent} {...LEGAL_LINK_PROPS}>
                обработку персональных данных
              </a>
            </span>
          </label>
          <span id="offer-error" className={`hint${offerError ? ' error' : ''}`}>
            {offerError
              ? 'Без принятия оферты оформить подписку нельзя — поставьте отметку'
              : 'Договор заключается принятием оферты, отдельный документ подписывать не нужно'}
          </span>

          {/* Автосписание относится к карте и только к ней: списывать по счёту
              нечего и не с чего. Показывать снятую галочку рядом со счётом
              значит предлагать согласие, которое ничего не даст. */}
          {method === 'card' ? (
            <>
              <label className="consent">
                <input
                  type="checkbox"
                  name="autopay"
                  checked={autopay}
                  onChange={(event) => setAutopay(event.target.checked)}
                />
                <span>
                  Разрешаю автоматически списывать плату за следующие периоды с этого
                  способа оплаты. Уведомление приходит за сутки до каждого списания,
                  согласие отзывается в кабинете в один клик
                </span>
              </label>
              <span className="hint">
                Без этой отметки подписка не продлевается сама: мы напомним письмом, и вы
                оплатите вручную
              </span>
            </>
          ) : (
            <p className="hint">
              Оплата по счёту не продлевается автоматически: перед концом периода мы
              пришлём счёт на следующий
            </p>
          )}
        </Panel>

        {/* Отказ у самой кнопки. Приёмка нажимала «Перейти к оплате», получала
            409 с готовым человеческим текстом — и не видела ничего: сообщение
            рисовалось в самом верху длинной страницы, а клиент в этот момент
            стоял внизу. Кнопка выглядела так, будто ничего не делает. */}
        {error ? (
          <div ref={errorRef} tabIndex={-1}>
            <ErrorNotice error={error} />
          </div>
        ) : null}

        <div className="pay-bar">
          <div>
            <div className="h3 tnum">{priceProse(amount)}</div>
            <p className="stamp">
              {chosen.title} · {period === 'year' ? 'год' : 'месяц'} ·{' '}
              {method === 'card' ? 'оплата на стороне ЮKassa' : 'счёт на юрлицо'}
              {period === 'year' ? ' · два месяца в подарок' : ''}
            </p>
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy
              ? method === 'card'
                ? 'Готовим платёж…'
                : 'Выставляем счёт…'
              : method === 'card'
                ? 'Перейти к оплате'
                : 'Выставить счёт'}
          </button>
        </div>

        <p className="prose-note">
          {method === 'card' ? (
            <>
              Дальше откроется страница ЮKassa. Мы не видим и не храним номер карты. После
              оплаты вернём вас сюда и покажем состояние платежа — подписка включится,
              когда банк подтвердит списание, а не в момент возврата.
            </>
          ) : (
            <>
              Дальше мы выпишем счёт с номером и покажем его здесь же. Оплачивать его
              будете вы со своего расчётного счёта — карта и ЮKassa в этом пути не
              участвуют. Замеры пойдут, когда деньги дойдут: мы увидим поступление и
              включим подписку сами.
            </>
          )}
        </p>
      </form>
    </>
  );
}
